import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import { query } from "../api/client";
import type { AuditRow } from "../api/types";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorBanner, LoadingState, Mono, PageHeader, when } from "../components/common";
import { MONO } from "../theme";

/**
 * The audit trail.
 *
 * Config is data here, so a config change has the blast radius of a deploy without
 * a commit history. This is that history: who changed what, and when — with
 * credentials stripped from the recorded payloads before they were ever written.
 */

const TARGET_KINDS = [
  "",
  "data_source",
  "signal",
  "market",
  "client",
  "subscription",
  "client_zone",
  "delivery_channel",
  "lead",
  "admin_user",
  "pipeline",
];

export function AuditPage() {
  const [targetKind, setTargetKind] = useState("");
  const audit = useResource<{ items: AuditRow[] }>(`/api/audit${query({ targetKind, limit: 200 })}`);

  return (
    <Box>
      <PageHeader
        title="Audit log"
        subtitle="Every console mutation, with the actor. Secrets are redacted before the row is written."
        actions={
          <Button startIcon={<RefreshIcon />} onClick={audit.reload}>
            Refresh
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <TextField
          select
          label="Target"
          value={targetKind}
          onChange={(e) => setTargetKind(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {TARGET_KINDS.map((kind) => (
            <MenuItem key={kind || "all"} value={kind}>
              {kind === "" ? "Everything" : kind}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      <ErrorBanner error={audit.error} onRetry={audit.reload} />
      {audit.loading && !audit.data ? <LoadingState /> : null}

      {audit.data && audit.data.items.length === 0 ? (
        <EmptyState title="Nothing recorded yet" hint="Entries appear as soon as anyone changes something." />
      ) : null}

      {audit.data && audit.data.items.length > 0 ? (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audit.data.items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{when(row.at)}</TableCell>
                  <TableCell>{row.actorEmail}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.action}
                      variant="outlined"
                      color={
                        row.action.endsWith(".delete")
                          ? "error"
                          : row.action === "auth.login_failed"
                            ? "warning"
                            : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Mono>{row.targetKind}</Mono>
                    {row.targetId ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.targetId.slice(0, 8)}…
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 480 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: MONO, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {Object.keys(row.detail).length === 0 ? "—" : JSON.stringify(row.detail)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}
    </Box>
  );
}
